
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowLeft } from 'lucide-react';
import { MessageBreederButton } from '@/components/messaging/MessageBreederButton';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface BuyerProfileData {
  id: string;
  first_name: string;
  last_name: string;
  profile_image_url: string | null;
  location: string | null;
  description: string | null;
}

const BuyerProfile = () => {
  const { id } = useParams();
  const { user } = useAuth();

  const { data: buyer, isLoading, error } = useQuery({
    queryKey: ['buyer-profile', id],
    queryFn: async () => {
      if (!id) throw new Error('Buyer ID is required');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image_url, location, description')
        .eq('id', id)
        .eq('user_type', 'buyer')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Buyer not found');
      
      return data as BuyerProfileData;
    },
    enabled: !!id,
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !buyer) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Buyer Not Found</h1>
            <p className="text-gray-600 mb-4">The buyer profile you're looking for doesn't exist.</p>
            <Link to="/marketplace" className="text-green-700 hover:text-green-800">
              Return to Marketplace
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const fullName = `${buyer.first_name} ${buyer.last_name}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link to="/marketplace" className="inline-flex items-center text-green-700 hover:text-green-800 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Link>

        {/* Profile Header */}
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
                {buyer.profile_image_url ? (
                  <img 
                    src={buyer.profile_image_url} 
                    alt={fullName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-green-700 flex items-center justify-center text-white font-semibold text-lg">
                    {getInitials(buyer.first_name, buyer.last_name)}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{fullName}</h1>
                  {buyer.location && (
                    <div className="flex items-center text-gray-600 mb-4">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span>{buyer.location}</span>
                    </div>
                  )}
                </div>
                {user && user.id !== buyer.id && (
                  <MessageBreederButton
                    listingId=""
                    breederId={buyer.id}
                    listingTitle={`Message ${fullName}`}
                    listingUrl={`/buyer/${buyer.id}`}
                    className="bg-green-700 hover:bg-green-800"
                  />
                )}
              </div>

              {buyer.description && (
                <p className="text-gray-700 leading-relaxed mb-6">{buyer.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BuyerProfile;
